import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Search, Wine, FlaskConical, X } from "lucide-react";
import { api } from "../lib/api";
import TonightsPour from "../components/TonightsPour";
import DiscoveriesStrip from "../components/DiscoveriesStrip";
import { SunRayMark, WaveDivider } from "../components/BrandMotifs";

const CATEGORY_ICON_COLOR = {
  gin: "#1A3628",
  whisky: "#8b5a2b",
  rum: "#a67034",
  tequila: "#b8a663",
  mezcal: "#7a5d3a",
  vodka: "#9ec0d8",
  liqueurs: "#b5667d",
  agave: "#a4b35a",
  aperitifs: "#c75a3a",
  wine: "#7a2434",
  beer: "#c79133",
  sake: "#d4d2c4",
  vermouth: "#8b3b3b",
  amaro: "#5e2a2a",
};

// Display order requested by user; anything else surfaces after these.
const CATEGORY_ORDER = ["gin", "whisky", "rum", "tequila", "vodka", "wine", "beer"];

function sortCategories(cats) {
  return [...cats].sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a.id);
    const bi = CATEGORY_ORDER.indexOf(b.id);
    if (ai === -1 && bi === -1) return (b.count || 0) - (a.count || 0);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

export default function LiquidAtlasHome() {
  const [cats, setCats] = useState([]);
  const [ginCount, setGinCount] = useState(92);

  useEffect(() => {
    api.get("/beverages/categories")
      .then((r) => {
        const all = r.data.categories || [];
        setCats(all);
        const gin = all.find((c) => c.id === "gin");
        if (gin?.count) setGinCount(gin.count);
      })
      .catch(() => {});
  }, []);

  const populated = useMemo(() => sortCategories(cats.filter((c) => (c.count || 0) > 0)), [cats]);
  const totalNonGin = cats.filter((c) => c.id !== "gin").reduce((a, c) => a + (c.count || 0), 0);

  return (
    <div className="bg-[#0E0F0C] text-white" data-testid="liquid-atlas-home">
      {/* ─── COMPACT HERO ─── */}
      <section className="relative px-5 md:px-12 pt-10 pb-8 md:pt-16 md:pb-12 overflow-hidden">
        <div className="absolute -top-32 -right-40 w-[600px] h-[600px] rounded-full opacity-25 pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(193,127,89,0.35), transparent 65%)" }} />
        {/* Subtle sun-ray watermark — extremely low opacity, inspired by brand language */}
        <SunRayMark size={420} className="absolute -top-24 right-4 md:right-16 hidden sm:block" opacity={0.05} />

        <div className="max-w-3xl mx-auto relative text-center md:text-left">
          <motion.p
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="overline text-[#C17F59] mb-3">
            BENO
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            data-testid="liquid-hero-title">
            BENO Liquid Atlas
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="text-base md:text-lg text-white/70 mt-4">
            Explore what's pouring tonight.
          </motion.p>

          {/* Search — real, route-aware, not faked */}
          <div className="mt-7 md:mt-9 max-w-xl mx-auto md:mx-0">
            <UnifiedSearch />
          </div>
        </div>
      </section>

      {/* ─── PRIMARY ACTION CARDS ─── */}
      <section className="px-5 md:px-12 pb-8" data-testid="primary-actions">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Gin Atlas — primary */}
          <Link to="/gin"
                data-testid="cta-gin-atlas"
                className="group relative bg-[#15171A] border border-[#C17F59]/30 hover:border-[#C17F59] transition-colors overflow-hidden">
            <div className="absolute -top-16 -right-20 w-56 h-56 rounded-full opacity-30 group-hover:opacity-60 transition-opacity duration-700"
                 style={{ background: "radial-gradient(circle, rgba(193,127,89,0.6), transparent 65%)" }} />
            <div className="relative p-6 md:p-7">
              <div className="flex items-center gap-3 mb-3">
                <FlaskConical size={16} className="text-[#C17F59]" />
                <span className="overline text-[#C17F59]">Flagship</span>
              </div>
              <h2 className="font-display text-2xl md:text-3xl mb-2">Gin Atlas</h2>
              <p className="text-sm text-white/65 leading-relaxed">
                India's largest gin collection, mapped by flavour, mood and story.
              </p>
              <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#C17F59] group-hover:text-[#D89A74] transition-colors">
                Enter Gin Atlas <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>

          {/* Spirits Menu — secondary */}
          <Link to="/spirits"
                data-testid="cta-spirits-menu"
                className="group relative bg-[#15171A] border border-white/10 hover:border-white/30 transition-colors overflow-hidden">
            <div className="absolute -bottom-16 -left-20 w-56 h-56 rounded-full opacity-25 group-hover:opacity-45 transition-opacity duration-700"
                 style={{ background: "radial-gradient(circle, rgba(139,90,43,0.7), transparent 65%)" }} />
            <div className="relative p-6 md:p-7">
              <div className="flex items-center gap-3 mb-3">
                <Wine size={16} className="text-white/70" />
                <span className="overline text-white/55">The full shelf</span>
              </div>
              <h2 className="font-display text-2xl md:text-3xl mb-2">Spirits Menu</h2>
              <p className="text-sm text-white/65 leading-relaxed">
                Whisky, rum, tequila, vodka, wine, beer and more.
              </p>
              <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-white/80 group-hover:text-[#C17F59] transition-colors">
                Browse Spirits <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* ─── TONIGHT'S POUR — heartbeat ─── */}
      <TonightsPour />

      {/* ─── CATEGORY QUICK NAV — only what's actually pouring ─── */}
      {populated.length > 0 && (
        <section className="px-5 md:px-12 py-12" data-testid="category-quick-nav">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-end justify-between mb-5">
              <div>
                <p className="overline text-[#C17F59] mb-1">On the menu tonight</p>
                <h2 className="font-display text-2xl md:text-3xl">
                  {ginCount + totalNonGin} bottles, ready to pour.
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
              {populated.map((c) => (
                <Link key={c.id}
                      to={c.id === "gin" ? "/gin/explore" : `/spirits/${c.id}`}
                      data-testid={`category-card-${c.id}`}
                      className="group relative bg-[#15171A] border border-white/8 hover:border-[#C17F59]/50 hover:bg-[#1A1C1F] p-4 transition-all active:scale-[0.98]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-2.5 h-2.5 rounded-full"
                         style={{ background: CATEGORY_ICON_COLOR[c.id] || "#888" }} />
                    <span className="text-[10px] tabular-nums text-white/45">
                      {c.count} {c.count === 1 ? "bottle" : "bottles"}
                    </span>
                  </div>
                  <h3 className="font-display text-xl group-hover:text-[#C17F59] transition-colors">
                    {c.label}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── FRESH DISCOVERIES ─── */}
      <DiscoveriesStrip />

      {/* ─── QUIET CLOSING ─── */}
      <section className="px-5 md:px-12 py-14">
        <div className="max-w-md mx-auto mb-8 opacity-60">
          <WaveDivider />
        </div>
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-hand text-2xl md:text-3xl text-[#D89A74] -rotate-1 mb-2" data-testid="liquid-closer">
            — the menu is the start. the bartender is the rest.
          </p>
        </div>
      </section>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// UNIFIED SEARCH — real client-side filter across gin atlas + non-gin SKUs
// ────────────────────────────────────────────────────────────────────────

function UnifiedSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [gins, setGins] = useState([]);
  const [bevs, setBevs] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  // Lazy-load once on first focus to keep the hero render fast.
  const ensureLoaded = () => {
    if (gins.length || bevs.length || loading) return;
    setLoading(true);
    Promise.all([
      api.get("/gins").then((r) => r.data?.gins || []).catch(() => []),
      api.get("/beverages?limit=500").then((r) => r.data?.beverages || []).catch(() => []),
    ]).then(([g, b]) => {
      setGins(g);
      setBevs(b);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const matches = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    const ginHits = gins
      .filter((g) =>
        g.name?.toLowerCase().includes(term) ||
        g.country?.toLowerCase().includes(term) ||
        (g.botanicals || []).some((bot) => bot.toLowerCase().includes(term)) ||
        (g.styles || []).some((s) => s.toLowerCase().includes(term))
      )
      .slice(0, 5)
      .map((g) => ({
        type: "gin",
        id: g.id,
        title: g.name,
        sub: `${g.country || ""}${g.price ? ` · ₹${g.price}` : ""}`,
        to: `/gin/g/${g.id}`,
        color: "#1A3628",
      }));
    const bevHits = bevs
      .filter((b) =>
        b.display_name?.toLowerCase().includes(term) ||
        b.brand?.toLowerCase().includes(term) ||
        b.category?.toLowerCase().includes(term) ||
        b.country?.toLowerCase().includes(term)
      )
      .slice(0, 5)
      .map((b) => ({
        type: b.category,
        id: b.id,
        title: b.display_name,
        sub: `${b.category}${b.country ? ` · ${b.country}` : ""}`,
        to: `/spirits/item/${b.id}`,
        color: CATEGORY_ICON_COLOR[b.category] || "#888",
      }));
    return [...ginHits, ...bevHits].slice(0, 8);
  }, [q, gins, bevs]);

  const submit = (e) => {
    if (e) e.preventDefault();
    const term = q.trim();
    if (!term) return;
    if (matches.length > 0) {
      navigate(matches[0].to);
    } else {
      // Fallback: send to gin explore with the query so they always land somewhere useful.
      navigate(`/gin/explore?q=${encodeURIComponent(term)}`);
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef} data-testid="liquid-search">
      <form onSubmit={submit} className="relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => { ensureLoaded(); if (q) setOpen(true); }}
          placeholder="Search gin, whisky, tequila, wine…"
          className="w-full bg-[#15171A] text-white placeholder:text-white/40 border border-white/10 focus:border-[#C17F59] focus:outline-none rounded-full py-3.5 pl-11 pr-12 text-base transition-colors"
          data-testid="liquid-search-input"
          aria-label="Search the BENO Liquid Atlas"
        />
        {q && (
          <button type="button" onClick={() => { setQ(""); setOpen(false); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white p-1"
                  data-testid="liquid-search-clear" aria-label="Clear search">
            <X size={14} />
          </button>
        )}
      </form>

      {/* Suggestion panel */}
      {open && q.trim().length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-[#15171A] border border-white/10 shadow-2xl z-30 max-h-96 overflow-y-auto text-left"
             data-testid="liquid-search-panel">
          {loading && (
            <p className="px-4 py-3 text-xs text-white/45 uppercase tracking-[0.22em]">Searching…</p>
          )}
          {!loading && matches.length === 0 && (
            <div className="px-4 py-4">
              <p className="text-xs uppercase tracking-[0.22em] text-white/45 mb-2">No live matches</p>
              <p className="text-sm text-white/65">
                Hit enter to search the Gin Atlas, or try a category below.
              </p>
            </div>
          )}
          {!loading && matches.map((m) => (
            <Link key={`${m.type}-${m.id}`} to={m.to}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 border-b border-white/5 hover:bg-[#1A1C1F] transition-colors last:border-b-0"
                  data-testid={`search-result-${m.id}`}>
              <div className="w-2 h-2 rounded-full shrink-0" style={{ background: m.color }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{m.title}</p>
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 mt-0.5 capitalize truncate">{m.sub}</p>
              </div>
              <ArrowRight size={12} className="text-white/30 shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
